import { useRef, useState } from "react";
import { useInView, useReducedMotion } from "motion/react";
import { LETTERS_PATH, MARK_INNER, STAR_PATH } from "@/components/brand/Logo";
import { AppDemo } from "./AppDemo";
import { C, PauseButton, Phone, Texture } from "./parts";

/*
 * Migrada's answer to Wise's card-behind-the-phone: a still life drawn in
 * code. Light paper, a patterned box, a paper boarding pass to Vienna tucked
 * under the phone (the app calls each stage a gate) and the phone running
 * the real app, in use (see AppDemo).
 */

/** A paper boarding pass: the move as a trip, with the Migrada star. */
function BoardingPass() {
  const notch = "radial-gradient(circle at 0 62%, transparent 9px, #FFFDF8 9.5px) left / 51% 100% no-repeat, radial-gradient(circle at 100% 62%, transparent 9px, #FFFDF8 9.5px) right / 51% 100% no-repeat";
  return (
    <div className="relative h-full w-full" style={{ background: notch, filter: "drop-shadow(10px 18px 14px rgba(10,15,14,0.28))", borderRadius: 10 }}>
      <div className="flex items-center justify-between rounded-t-[10px] px-[7%] py-[5%]" style={{ backgroundColor: C.TEAL }}>
        <span className="text-[clamp(7px,1.9cqi,11px)] font-black uppercase tracking-[0.14em]" style={{ color: C.BRAND_DARK }}>
          Boarding pass
        </span>
        <svg viewBox="48 13 11 10.2" className="h-[1.4em] w-[1.4em] text-[clamp(7px,1.9cqi,11px)]" aria-hidden="true">
          <path d={STAR_PATH} fill={C.BRAND_DARK} />
        </svg>
      </div>
      <div className="px-[7%] pt-[6%]" style={{ color: C.INK }}>
        <div className="flex items-end justify-between">
          <span>
            <span className="block text-[clamp(6px,1.5cqi,9px)] font-bold uppercase tracking-[0.12em]" style={{ color: C.MUTED }}>
              From
            </span>
            <span className="block text-[clamp(14px,4.6cqi,28px)] font-black leading-none tracking-[-0.03em]">HOME</span>
          </span>
          <span className="pb-[2%] text-[clamp(10px,3cqi,18px)] font-black" style={{ color: C.TEAL_DEEP }}>
            →
          </span>
          <span className="text-right">
            <span className="block text-[clamp(6px,1.5cqi,9px)] font-bold uppercase tracking-[0.12em]" style={{ color: C.MUTED }}>
              To
            </span>
            <span className="block text-[clamp(14px,4.6cqi,28px)] font-black leading-none tracking-[-0.03em]">VIE</span>
          </span>
        </div>
        <div className="mt-[9%] border-t border-dashed pt-[6%]" style={{ borderColor: C.RULE }}>
          <div className="flex justify-between text-[clamp(6px,1.5cqi,9px)] font-bold uppercase tracking-[0.12em]" style={{ color: C.MUTED }}>
            <span>Gate</span>
            <span>Seat</span>
          </div>
          <div className="flex justify-between text-[clamp(10px,3cqi,17px)] font-black tracking-[-0.02em]">
            <span>4 / 7</span>
            <span>1A</span>
          </div>
          <div className="mt-[8%] flex h-[clamp(14px,4cqi,24px)] gap-[1.5px]" aria-hidden="true">
            {Array.from({ length: 34 }).map((_, i) => (
              <span key={i} className="h-full rounded-[0.5px]" style={{ width: [1, 2, 1, 3, 1, 2][i % 6], backgroundColor: C.INK }} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/** A small round sticker with the mark, stuck to the box. */
function Sticker() {
  return (
    <svg viewBox="0 0 64 64" className="h-full w-full" style={{ filter: "drop-shadow(2px 4px 4px rgba(10,15,14,0.3))" }} aria-hidden="true">
      <circle cx="32" cy="32" r="31" fill={C.TEAL} />
      <g transform={MARK_INNER}>
        <path d={STAR_PATH} fill={C.BRAND_DARK} />
        <path d={LETTERS_PATH} fill={C.BRAND_DARK} />
      </g>
    </svg>
  );
}

/**
 * The desk, seen from a little further away than before, with the app
 * playing on the phone. It plays only while on screen; the pause button
 * stops it. Reduced motion shows the home screen, still.
 */
export function AppDesk() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { margin: "-15% 0px" });
  const reduced = useReducedMotion() ?? false;
  const [paused, setPaused] = useState(false);

  return (
    <figure
      ref={ref}
      className="relative isolate w-full overflow-hidden [container-type:inline-size]"
      style={{ aspectRatio: "4 / 5", borderRadius: 40, background: "radial-gradient(120% 90% at 15% 0%, #FBFAF6 0%, #ECE8DF 55%, #DFDAD0 100%)" }}
      aria-label="The Migrada app in use: checking the route, opening the Money stage, uploading a bank statement and seeing it checked."
    >
      {/* The box: a thick lid with a pattern, lying at an angle. */}
      <div className="absolute left-[16%] top-[13%] h-[74%] w-[78%]" style={{ transform: "rotate(-12deg)" }} aria-hidden="true">
        <div className="absolute inset-0 translate-x-[2.2%] translate-y-[1.8%] rounded-[22px]" style={{ backgroundColor: "#00221F", boxShadow: "40px 60px 70px -10px rgba(10,15,14,0.45)" }} />
        <div className="absolute inset-0 overflow-hidden rounded-[22px]" style={{ background: "linear-gradient(135deg, #00443E 0%, #00332F 45%, #002A26 100%)" }}>
          <Texture color="rgba(46,230,214,0.32)" />
        </div>
        <div className="absolute bottom-[8%] right-[9%] h-[14%] w-auto" style={{ aspectRatio: "1 / 1", transform: "rotate(10deg)" }}>
          <Sticker />
        </div>
      </div>

      {/* The boarding pass, tucked under the phone. */}
      <div className="absolute left-[52%] top-[24%] w-[38%]" style={{ aspectRatio: "0.78 / 1", transform: "rotate(13deg)" }} aria-hidden="true">
        <BoardingPass />
      </div>

      {/* The phone, the light coming from the top left. */}
      <div className="absolute left-[27%] top-[11%] w-[44%]" style={{ filter: "drop-shadow(22px 30px 24px rgba(10,15,14,0.35))" }} aria-hidden="true">
        <Phone width="100%" tilt={-3}>
          <AppDemo running={inView && !paused && !reduced} />
        </Phone>
      </div>

      <PauseButton paused={paused} onToggle={() => setPaused((p) => !p)} hidden={reduced} />
    </figure>
  );
}
